// components/GoogleSignInButton.jsx
// KEY FIX: Google's SDK must be initialized ONCE with a stable callback.
// We use a ref (onSuccessRef) to always point to the latest onSuccess prop
// without ever re-initializing the SDK. This prevents the blank page bug
// caused by re-initialization on every parent re-render.

import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext";

export default function GoogleSignInButton({ onSuccess, onError }) {
  const { dark } = useTheme();
  const containerRef  = useRef(null);
  const initialized   = useRef(false);
  const onSuccessRef  = useRef(onSuccess);  // always current, never stale
  const onErrorRef    = useRef(onError);
  const [ready,      setReady]      = useState(false);
  const [scriptErr,  setScriptErr]  = useState(false);

  // Keep refs current on every render — no re-init needed
  onSuccessRef.current = onSuccess;
  onErrorRef.current   = onError;

  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Stable callback passed to Google — reads from ref so always has latest handler
  const stableCallback = useRef((response) => {
    try {
      // response.credential is the Google ID token string
      const result = onSuccessRef.current(response.credential);
      if (result && typeof result.catch === "function") {
        result.catch((err) => onErrorRef.current?.(err.message || "Google sign-in failed"));
      }
    } catch (err) {
      onErrorRef.current?.(err.message || "Google sign-in failed");
    }
  });

  // Initialize Google SDK once
  useEffect(() => {
    if (!clientId) { setScriptErr(true); return; }
    if (initialized.current) return;

    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      if (window.google?.accounts?.id) {
        clearInterval(poll);
        initialized.current = true;
        // Initialize with the stable ref-based callback — never re-run this
        window.google.accounts.id.initialize({
          client_id:              clientId,
          callback:               stableCallback.current,
          cancel_on_tap_outside:  true,
        });
        setReady(true);
      } else if (attempts > 50) {
        clearInterval(poll);
        setScriptErr(true);
      }
    }, 100);

    return () => clearInterval(poll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Render the button whenever ready or theme changes
  useEffect(() => {
    if (!ready || !containerRef.current || !window.google?.accounts?.id) return;
    containerRef.current.innerHTML = "";
    window.google.accounts.id.renderButton(containerRef.current, {
      theme:          dark ? "filled_black" : "outline",
      size:           "large",
      width:          containerRef.current.offsetWidth || 356,
      text:           "continue_with",
      shape:          "rectangular",
      logo_alignment: "left",
    });
  }, [ready, dark]);

  // Fallback when Google script not available
  if (scriptErr || !clientId) {
    return (
      <div style={{
        border: "0.5px solid #ddd", borderRadius: 10,
        padding: "12px 16px", background: "#fafafa",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <GoogleLogo />
        <span style={{ fontSize: 13, color: "#888" }}>
          {!clientId
            ? "Set REACT_APP_GOOGLE_CLIENT_ID in client/.env"
            : "Google Sign-In unavailable — check network"}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%", minHeight: 44,
        borderRadius: 8, overflow: "hidden",
        background: ready ? "transparent" : "#f4f4f4",
        transition: "background 0.2s",
      }}
    />
  );
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}
